import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    number: '01',
    title: 'Choose Your Meals',
    description: 'Browse our weekly menu and select your favorite dishes from our chef-curated selection.',
    image: 'https://readdy.ai/api/search-image?query=person%20using%20smartphone%20app%20to%20browse%20and%20select%20healthy%20meal%20options%20from%20digital%20menu%2C%20modern%20UI%20on%20phone%20screen%2C%20colorful%20food%20photos%20visible%2C%20clean%20white%20background%2C%20warm%20natural%20lighting%2C%20top%20down%20view%2C%20lifestyle%20photography&width=400&height=400&seq=step-choose-001&orientation=squarish',
  },
  {
    number: '02',
    title: 'We Cook Fresh Daily',
    description: 'Our chefs prepare your meals fresh every day using premium regional ingredients.',
    image: 'https://readdy.ai/api/search-image?query=professional%20chef%20in%20white%20uniform%20carefully%20plating%20fresh%20colorful%20healthy%20meal%20in%20modern%20commercial%20kitchen%2C%20steam%20rising%2C%20vibrant%20vegetables%20and%20protein%2C%20golden%20light%2C%20close%20up%20action%20shot%2C%20authentic%20culinary%20photography&width=400&height=400&seq=step-cook-001&orientation=squarish',
  },
  {
    number: '03',
    title: 'Fast Chilled Delivery',
    description: 'Meals are delivered to your door in temperature-controlled packaging.',
    image: 'https://readdy.ai/api/search-image?query=delivery%20courier%20holding%20insulated%20thermal%20food%20bag%20at%20front%20door%20of%20home%2C%20wearing%20uniform%2C%20smiling%20friendly%2C%20bright%20daytime%20suburban%20neighborhood%2C%20clean%20modern%20packaging%2C%20lifestyle%20photography%20warm%20tones&width=400&height=400&seq=step-deliver-001&orientation=squarish',
  },
  {
    number: '04',
    title: 'Heat & Enjoy',
    description: 'Simply heat and enjoy restaurant-quality meals in minutes.',
    image: 'https://readdy.ai/api/search-image?query=happy%20person%20sitting%20at%20dining%20table%20enjoying%20beautiful%20restaurant%20quality%20healthy%20meal%20at%20home%2C%20elegant%20plate%20with%20colorful%20food%2C%20warm%20cozy%20interior%2C%20soft%20natural%20window%20light%2C%20lifestyle%20food%20photography%2C%20relaxed%20and%20satisfied%20expression&width=400&height=400&seq=step-enjoy-001&orientation=squarish',
  },
];

export default function HowItWorks() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-gradient-to-b from-white to-primary-50/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-primary-500 text-sm font-semibold tracking-wider uppercase">
            The Process
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            From Kitchen to Your Door in 4 Simple Steps
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            We handle everything from sourcing to cooking to delivery, so you can focus on enjoying delicious, healthy meals.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`group relative bg-white rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 cursor-pointer ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Step Number Badge */}
              <div className="absolute top-6 right-6 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-primary-500 font-bold text-sm">{step.number}</span>
              </div>

              {/* Image — replaces icon */}
              <div className="relative w-full h-44 rounded-2xl overflow-hidden mb-6">
                <img
                  src={step.image}
                  alt={step.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                {step.title}
              </h3>
              <p className="text-gray-600 text-center leading-relaxed">
                {step.description}
              </p>

              {/* Connecting Line (except last item) */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-primary-300 to-transparent"></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}